<template>
    <div class="bg-white rounded-lg border border-gray-200">
        <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-lg font-medium text-gray-900">Notification Settings</h3>
        </div>
        <div class="px-4 py-3">
            <!-- Push Permission Banner -->
            <div v-if="pushPermission === 'default'" class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
                <div class="text-sm text-blue-800">
                    Enable desktop notifications to get real-time alerts.
                </div>
                <button @click="enablePush" class="ml-4 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                    Enable
                </button>
            </div>
            <div v-else-if="pushPermission === 'denied'" class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div class="text-sm text-yellow-800">
                    Desktop notifications are blocked. Please enable them in your browser settings.
                </div>
            </div>

            <table class="w-full text-sm">
                <thead>
                    <tr class="border-b border-gray-200">
                        <th class="text-left py-2 font-medium text-gray-700">Event</th>
                        <th class="text-center py-2 font-medium text-gray-700 w-20">Email</th>
                        <th class="text-center py-2 font-medium text-gray-700 w-20">Push</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="event in notificationEvents" :key="event.key" class="border-b border-gray-100">
                        <td class="py-3">
                            <div class="font-medium text-gray-700">{{ event.label }}</div>
                            <div class="text-xs text-gray-500">{{ event.description }}</div>
                        </td>
                        <td class="text-center py-3">
                            <input
                                type="checkbox"
                                v-model="prefs[event.key].email"
                                @change="debouncedSave"
                                class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            >
                        </td>
                        <td class="text-center py-3">
                            <input
                                type="checkbox"
                                v-model="prefs[event.key].push"
                                @change="debouncedSave"
                                class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                :disabled="pushPermission !== 'granted'"
                            >
                        </td>
                    </tr>
                </tbody>
            </table>

            <div v-if="saving" class="mt-2 text-xs text-gray-500">Saving...</div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        initialPrefs: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            prefs: JSON.parse(JSON.stringify(this.initialPrefs)),
            saving: false,
            saveTimeout: null,
            pushPermission: 'default',
            notificationEvents: [
                { key: 'admin_coder_applied', label: 'Coder Application', description: 'When a coder applies to join the platform' },
                { key: 'admin_order_submitted', label: 'Order Submitted', description: 'When an agency submits a new order' },
                { key: 'admin_agency_signup', label: 'Agency Signup', description: 'When a new agency registers on the platform' },
                { key: 'admin_problem_created', label: 'Problem Reported', description: 'When a coder reports a problem with an assignment' },
                { key: 'admin_problem_resolved', label: 'Problem Resolved', description: 'When a problem is resolved' },
                { key: 'admin_new_assignments', label: 'New Assignments', description: 'When new assignments become available' },
                { key: 'admin_urgent_assignment', label: 'Urgent Assignment', description: 'When an urgent assignment is added' },
                { key: 'admin_assignment_reopened', label: 'Assignment Reopened', description: 'When a completed assignment is reopened' },
                { key: 'admin_assignment_completed', label: 'Assignment Completed', description: 'When a coder completes an assignment' },
            ],
        };
    },

    mounted() {
        if ('Notification' in window) {
            this.pushPermission = Notification.permission;
        }
        if (this.pushPermission === 'granted' && window.PushNotifications) {
            window.PushNotifications.ensureSubscription();
        }
    },

    methods: {
        debouncedSave() {
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
            }
            this.saveTimeout = setTimeout(() => {
                this.save();
            }, 1000);
        },

        async save() {
            this.saving = true;
            try {
                await axios.patch('/site_admins/settings/notifications', {
                    notification_prefs: this.prefs,
                });
                this.$toast.success('Notification settings saved');
            } catch (error) {
                this.$toast.error('Failed to save notification settings');
            } finally {
                this.saving = false;
            }
        },

        async enablePush() {
            if (!window.PushNotifications) {
                this.$toast.error('Push notifications are not available');
                return;
            }
            try {
                const result = await window.PushNotifications.subscribe();
                this.pushPermission = Notification.permission;
                if (result) {
                    this.$toast.success('Desktop notifications enabled');
                } else if (Notification.permission === 'denied') {
                    this.$toast.error('Notifications were blocked by your browser');
                }
            } catch (e) {
                console.error('Push subscription failed:', e);
                this.$toast.error('Failed to enable notifications. Please try again later.');
            }
        },
    },
};
</script>
